// Client/src/core/SessionManager.ts
// Verwaltet Authentifizierungs- und Sitzungszustand zentral.

import { HttpClient, HttpError } from './HttpClient';
import {
  isNativeDevSimulatorEnabled,
  setNativeDevSimulatorSession,
} from '../utils/nativeClientEnv';
import { clearProfileSignatureSessionCache } from '../utils/profileSignatureCrypto';

export interface SessionUser {
  id: string;
  employee_id: string;
  first_name: string;
  last_name: string;
  role: string;
  storeIds?: string[];
  mustChangePassword?: boolean;
  qrLoginEnabled?: boolean;
  notificationEmail?: string | null;
  permissions?: string[];
  sessionKind?: 'default' | 'anonymous_guest' | 'native_dev_sim';
  isAnonymousGuest?: boolean;
  guestDeviceUuid?: string | null;
  guestDisplayName?: string | null;
  guestExpiresAt?: string | null;
  guestStoreId?: string | null;
  hasProfileSignature?: boolean;
  profileSignatureUpdatedAt?: string | null;
  layoutDesignerContext?: {
    storeCode?: string;
  };
}

export interface SessionStoreSettings {
  ferienBundeslaender?: string[] | null;
  revisionsEnabled?: number;
  [key: string]: unknown;
}

export interface SessionStore {
  id: string;
  storeId?: string;
  store_id?: string;
  name?: string;
  location?: string;
  generateReport?: boolean;
  hmd_date?: string | null;
  settings?: SessionStoreSettings;
}

export type SessionStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated' | 'error';

export interface SessionSnapshot {
  status: SessionStatus;
  token: string | null;
  user: SessionUser | null;
  stores: SessionStore[];
  selectedStoreId: string | null;
  error: string | null;
}

type SessionListener = (snapshot: SessionSnapshot) => void;

interface LoginPayload {
  employeeId: string;
  password?: string;
  qr_token?: string;
}

interface LoginResponse {
  token?: string;
  user?: SessionUser | null;
  stores?: SessionStore[];
  selectedStoreId?: string | null;
}

interface TokenResponse {
  token: string;
  user: {
    id: string;
    employee_id: string;
    first_name: string;
    last_name: string;
    role: string;
    storeIds?: string[];
    mustChangePassword?: boolean;
    qrLoginEnabled?: boolean;
    notificationEmail?: string | null;
    permissions?: string[];
    sessionKind?: 'default' | 'anonymous_guest' | 'native_dev_sim';
    isAnonymousGuest?: boolean;
    guestDeviceUuid?: string | null;
    guestDisplayName?: string | null;
    guestExpiresAt?: string | null;
    guestStoreId?: string | null;
    hasProfileSignature?: boolean;
    profileSignatureUpdatedAt?: string | null;
    layoutDesignerContext?: {
      storeCode?: string;
    };
  };
  stores?: SessionStore[];
  selectedStoreId?: string | null;
}

interface GuestConsumePayload {
  challengeToken: string;
  deviceUuid: string;
  displayName?: string;
}

interface NativeClientLoginPayload {
  store_id: string;
  public_key: string;
  token: string;
  username: string;
  dev_sim?: boolean;
}

interface NativeLoginSuccess {
  status: 'authenticated';
  token: string;
  user: SessionUser | null;
  serverPublicKey?: string;
}

interface NativeLoginCreated {
  status: 'created';
  serverPublicKey?: string;
  message?: string;
}

interface NativeLoginPending {
  status: 'pending_activation';
  serverPublicKey?: string;
  message?: string;
}

interface NativeLoginUnauthorized {
  status: 'unauthorized';
  serverPublicKey?: string;
  message: string;
}

interface NativeLoginError {
  status: 'error';
  message: string;
  statusCode?: number;
}

export type NativeLoginResult =
  | NativeLoginSuccess
  | NativeLoginCreated
  | NativeLoginPending
  | NativeLoginUnauthorized
  | NativeLoginError;

const INITIAL_STATE: SessionSnapshot = {
  status: 'idle',
  token: null,
  user: null,
  stores: [],
  selectedStoreId: null,
  error: null,
};

export class SessionManager {
  private state: SessionSnapshot = { ...INITIAL_STATE };
  private listeners = new Set<SessionListener>();

  constructor(private readonly http: HttpClient) {}

  subscribe(listener: SessionListener) {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  getSnapshot(): SessionSnapshot {
    return this.state;
  }

  getToken(): string | null {
    return this.state.token;
  }

  async bootstrapFromServer(): Promise<SessionSnapshot> {
    this.updateState({ status: 'loading', error: null });

    try {
      const response = await this.http.get<TokenResponse>('/api/users/token');
      const user = this.normalizeUser(response.user);
      const stores = response.stores ?? [];
      const selectedStoreId = response.selectedStoreId ?? null;

      if (isNativeDevSimulatorEnabled() && user?.role !== 'tablet') {
        setNativeDevSimulatorSession(false);
        this.resetSession('unauthenticated');
        return this.state;
      }

      this.updateState({
        status: 'authenticated',
        token: response.token ?? null,
        user,
        selectedStoreId,
        error: null,
      });

      this.updateStores(stores);
      return this.state;
    } catch (error) {
      this.handleAuthError(error);
      throw error;
    }
  }

  async login(payload: LoginPayload): Promise<SessionSnapshot> {
    this.updateState({ status: 'loading', error: null });

    try {
      const response = await this.http.post<LoginResponse>('/api/auth/login', payload);
      const user = response.user ? this.normalizeUser(response.user) : null;
      const nextState: Partial<SessionSnapshot> = {
        status: 'authenticated',
        token: response.token ?? null,
        user,
        selectedStoreId: response.selectedStoreId ?? null,
        error: null,
      };
      this.updateState(nextState);
      this.updateStores(response.stores ?? []);
      return this.state;
    } catch (error) {
      this.handleAuthError(error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await this.http.post('/api/auth/logout');
    } catch (error) {
      // Logout-Fehler ignorieren, Session wird trotzdem geleert
      console.warn('Logout fehlgeschlagen oder nicht möglich:', error);
    } finally {
      setNativeDevSimulatorSession(false);
      this.resetSession('unauthenticated');
    }
  }

  async consumeGuestLogin(payload: GuestConsumePayload): Promise<SessionSnapshot> {
    this.updateState({ status: 'loading', error: null });

    try {
      const response = await this.http.post<LoginResponse>('/api/auth/guest-qr/consume', payload, {
        auth: false,
      });
      const user = response.user ? this.normalizeUser(response.user) : null;
      this.updateState({
        status: 'authenticated',
        token: response.token ?? null,
        user,
        selectedStoreId: response.selectedStoreId ?? null,
        error: null,
      });
      this.updateStores(response.stores ?? []);
      return this.state;
    } catch (error) {
      this.handleAuthError(error);
      throw error;
    }
  }

  async loginNative(payload: NativeClientLoginPayload): Promise<NativeLoginResult> {
    this.updateState({ status: 'loading', error: null });

    try {
      const response = await this.http.requestWithMeta<LoginResponse & { serverPublicKey?: string; message?: string }>(
        '/api/auth/native_client',
        {
          method: 'POST',
          body: payload,
          auth: false,
        }
      );

      if (response.status === 200) {
        const user = response.data.user ? this.normalizeUser(response.data.user) : null;
        setNativeDevSimulatorSession(Boolean(payload.dev_sim || user?.sessionKind === 'native_dev_sim'));
        const nextState: Partial<SessionSnapshot> = {
          status: 'authenticated',
          token: response.data.token ?? null,
          user,
          selectedStoreId: (response.data as LoginResponse).selectedStoreId ?? null,
          error: null,
        };
        this.updateState(nextState);
        this.updateStores((response.data as LoginResponse).stores ?? []);
        return {
          status: 'authenticated',
          token: response.data.token ?? '',
          user,
          serverPublicKey: response.data.serverPublicKey,
        };
      }

      if (response.status === 201) {
        setNativeDevSimulatorSession(false);
        this.updateState({ status: 'unauthenticated' });
        return {
          status: 'created',
          serverPublicKey: response.data.serverPublicKey,
          message: response.data.message,
        };
      }

      setNativeDevSimulatorSession(false);
      this.updateState({ status: 'unauthenticated' });
      return {
        status: 'error',
        statusCode: response.status,
        message: response.data?.message || 'Unbekannter Fehler beim Native-Login',
      };
    } catch (error) {
      if (error instanceof HttpError) {
        if (error.status === 423) {
          setNativeDevSimulatorSession(false);
          this.updateState({ status: 'unauthenticated' });
          const data = (error.data ?? {}) as { serverPublicKey?: string; message?: string };
          return {
            status: 'pending_activation',
            serverPublicKey: data.serverPublicKey,
            message: data.message ?? error.message,
          };
        }

        if (error.status === 401) {
          setNativeDevSimulatorSession(false);
          this.updateState({ status: 'unauthenticated' });
          const data = (error.data ?? {}) as { serverPublicKey?: string; message?: string };
          return {
            status: 'unauthorized',
            serverPublicKey: data.serverPublicKey,
            message: data.message ?? error.message,
          };
        }

        this.updateState({
          status: 'error',
          error: error.message,
          token: null,
          user: null,
        });

        return {
          status: 'error',
          statusCode: error.status,
          message: error.message,
        };
      }

      const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
      setNativeDevSimulatorSession(false);
      this.updateState({
        status: 'error',
        error: message,
      });

      return {
        status: 'error',
        message,
      };
    }
  }

  setSelectedStore(storeId: string | null) {
    const previousSelected = this.state.selectedStoreId;

    if (!storeId) {
      if (previousSelected !== null) {
        this.updateState({ selectedStoreId: null });
        void this.persistSelectedStore(null);
      }
      return;
    }

    const isValid = this.state.stores.some((store) => store.id === storeId);
    const nextSelected = isValid ? storeId : null;

    if (nextSelected === previousSelected) {
      return;
    }

    this.updateState({ selectedStoreId: nextSelected });
    void this.persistSelectedStore(nextSelected);
  }

  updateStores(stores: SessionStore[]) {
    const previousSelected = this.state.selectedStoreId;
    const normalisedStores = (stores || [])
      .map((store) => ({
        ...store,
        id: store.id ?? store.store_id ?? store.storeId ?? '',
      }))
      .filter((store) => Boolean(store.id));

    const selectedStoreId = this.pickSelectedStore(normalisedStores, this.state.selectedStoreId, this.state.user?.storeIds);
    this.updateState({
      stores: normalisedStores,
      selectedStoreId,
    });

    if (selectedStoreId !== previousSelected) {
      void this.persistSelectedStore(selectedStoreId);
    }
  }

  ensureSelectedStore() {
    const previousSelected = this.state.selectedStoreId;
    const selected = this.pickSelectedStore(this.state.stores, this.state.selectedStoreId, this.state.user?.storeIds);
    if (selected !== previousSelected) {
      this.updateState({ selectedStoreId: selected });
      void this.persistSelectedStore(selected);
    }
    return selected;
  }

  async refreshUserProfile(): Promise<void> {
    if (!this.state.token) {
      return;
    }

    try {
      const profile = await this.http.get<{
        id: string;
        employee_id: string;
        first_name: string;
        last_name: string;
        role: string;
        sessionKind?: 'default' | 'anonymous_guest' | 'native_dev_sim';
        notificationEmail?: string | null;
        permissions?: string[];
        isAnonymousGuest?: boolean;
        guestDeviceUuid?: string | null;
        guestDisplayName?: string | null;
        guestExpiresAt?: string | null;
        guestStoreId?: string | null;
        hasProfileSignature?: boolean;
        profileSignatureUpdatedAt?: string | null;
        layoutDesignerContext?: {
          storeCode?: string;
        };
        stores?: { id: string }[];
      }>('/api/auth/profile');
      this.updateState({
        user: {
          id: profile.id,
          employee_id: profile.employee_id,
          first_name: profile.first_name,
          last_name: profile.last_name,
          role: profile.role,
          storeIds: Array.isArray(profile.stores) ? profile.stores.map((store) => store.id) : this.state.user?.storeIds,
          mustChangePassword: this.state.user?.mustChangePassword,
          qrLoginEnabled: this.state.user?.qrLoginEnabled,
          notificationEmail: profile.notificationEmail ?? this.state.user?.notificationEmail ?? null,
          permissions: Array.isArray(profile.permissions) ? profile.permissions : this.state.user?.permissions ?? [],
          sessionKind: profile.sessionKind ?? this.state.user?.sessionKind,
          isAnonymousGuest: profile.isAnonymousGuest ?? this.state.user?.isAnonymousGuest,
          guestDeviceUuid: profile.guestDeviceUuid ?? this.state.user?.guestDeviceUuid ?? null,
          guestDisplayName: profile.guestDisplayName ?? this.state.user?.guestDisplayName ?? null,
          guestExpiresAt: profile.guestExpiresAt ?? this.state.user?.guestExpiresAt ?? null,
          guestStoreId: profile.guestStoreId ?? this.state.user?.guestStoreId ?? null,
          hasProfileSignature: profile.hasProfileSignature ?? this.state.user?.hasProfileSignature ?? false,
          profileSignatureUpdatedAt: profile.profileSignatureUpdatedAt ?? this.state.user?.profileSignatureUpdatedAt ?? null,
          layoutDesignerContext: profile.layoutDesignerContext ?? this.state.user?.layoutDesignerContext ?? {},
        },
      });
    } catch (error) {
      console.error('Fehler beim Laden des Benutzerprofils:', error);
      if (error instanceof HttpError && error.status === 401) {
        this.resetSession('unauthenticated', 'Sitzung nicht mehr gültig');
      }
      throw error;
    }
  }

  private async persistSelectedStore(storeId: string | null): Promise<void> {
    if (this.state.status !== 'authenticated') {
      return;
    }

    try {
      await this.http.post('/api/auth/selected-store', { selectedStoreId: storeId });
    } catch (error) {
      console.warn('Ausgewählter Markt konnte nicht serverseitig gespeichert werden:', error);
    }
  }

  private normalizeUser(payload: SessionUser | TokenResponse['user'] | undefined | null): SessionUser | null {
    if (!payload) {
      return null;
    }

    return {
      id: payload.id,
      employee_id: payload.employee_id,
      first_name: payload.first_name,
      last_name: payload.last_name,
      role: payload.role,
      storeIds: Array.isArray((payload as SessionUser).storeIds)
        ? [...(payload as SessionUser).storeIds!]
        : Array.isArray((payload as TokenResponse['user']).storeIds)
          ? [...(payload as TokenResponse['user']).storeIds!]
          : undefined,
      mustChangePassword: (payload as SessionUser).mustChangePassword,
      qrLoginEnabled: (payload as SessionUser).qrLoginEnabled,
      notificationEmail: (payload as SessionUser).notificationEmail ?? null,
      permissions: Array.isArray((payload as SessionUser).permissions) ? [...(payload as SessionUser).permissions!] : [],
      sessionKind: (payload as SessionUser).sessionKind,
      isAnonymousGuest: (payload as SessionUser).isAnonymousGuest,
      guestDeviceUuid: (payload as SessionUser).guestDeviceUuid ?? null,
      guestDisplayName: (payload as SessionUser).guestDisplayName ?? null,
      guestExpiresAt: (payload as SessionUser).guestExpiresAt ?? null,
      guestStoreId: (payload as SessionUser).guestStoreId ?? null,
      hasProfileSignature: (payload as SessionUser).hasProfileSignature ?? false,
      profileSignatureUpdatedAt: (payload as SessionUser).profileSignatureUpdatedAt ?? null,
      layoutDesignerContext: (payload as SessionUser).layoutDesignerContext ?? {},
    };
  }

  private pickSelectedStore(
    stores: SessionStore[],
    currentSelected: string | null,
    userStoreIds?: string[]
  ): string | null {
    if (currentSelected && stores.some((store) => store.id === currentSelected)) {
      return currentSelected;
    }

    if (userStoreIds && userStoreIds.length > 0) {
      const primary = userStoreIds.find((id) => stores.some((store) => store.id === id));
      if (primary) {
        return primary;
      }
    }

    if (stores.length > 0) {
      return stores[0].id;
    }

    return null;
  }

  private handleAuthError(error: unknown) {
    if (error instanceof HttpError) {
      if (error.status === 401) {
        this.resetSession('unauthenticated','Unbekannter Fehler');
        return;
      }

      this.updateState({
        status: 'error',
        error:'Unbekannter Fehler',
        token: null,
        user: null,
      });
      return;
    }

    const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
    this.updateState({
      status: 'error',
      error: message,
      token: null,
      user: null,
    });
  }

  private updateState(patch: Partial<SessionSnapshot>) {
    this.state = {
      ...this.state,
      ...patch,
    };
    this.notify();
  }

  private resetSession(status: SessionStatus = 'idle', error: string | null = null) {
    void clearProfileSignatureSessionCache();
    this.state = {
      ...INITIAL_STATE,
      status,
      error,
    };
    this.notify();
  }

  private notify() {
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }
}

let sessionManagerRef: SessionManager | null = null;

const httpClient = new HttpClient({
  getAccessToken: () => sessionManagerRef?.getToken() ?? null,
});

const manager = new SessionManager(httpClient);
sessionManagerRef = manager;

export const sessionManager = manager;
export { httpClient };
